import {
  CreateFileRequestType,
  CreateScreenRequestType,
  createScreenRequest,
  fetchFilesRequest,
  createFileRequest,
  deleteFileRequest,
  editFileRequest,
  EndVideoFileRequestType,
  startDownloadVideoFileRequest,
  StartVideoFileRequestType,
  partDownloadVideoFileRequest,
  PartVideoFileRequestType,
} from 'api';
import {
  abortVideoUploadRequest,
  deleteFilesRequest,
  endDownloadVideoFileRequest,
} from 'api/filesApi/filesApi';
import { AbortVideoFileRequestType, EditFileRequestType } from 'api/filesApi/types';
import { setAuthLoading, setFiles } from 'state/actions';
import { TypedDispatch } from 'state/types';

export const fetchFiles =
  (inspectionId: string, silent?: boolean) => async (dispatch: TypedDispatch) => {
    try {
      dispatch(setAuthLoading(true));
      const response = await fetchFilesRequest({ inspectionId, take: 100 }, silent);

      // Don't clobber already-loaded files with an empty/failed response.
      if (response?.data?.rows) {
        dispatch(setFiles(response.data.rows));
      }
    } finally {
      dispatch(setAuthLoading(false));
    }
  };

export const createFile =
  (
    file: CreateFileRequestType,
    callback: (percentCompleted: number, name: string) => void,
    signal: AbortSignal,
    setNotification: (notification: string) => void,
  ) =>
  async () => {
    try {
      setNotification(
        "Attention! If you close this page, file uploading will be interrupted, and the files won't be uploaded to our servers. Please ensure that the upload process is complete before closing the page. Thank you for your understanding!",
      );

      return await createFileRequest(file, callback, signal);
    } finally {
      setNotification('');
    }
  };

export const editFile =
  (file: EditFileRequestType & { id: string }) => async (dispatch: TypedDispatch) => {
    try {
      dispatch(setAuthLoading(true));
      const response = await editFileRequest(file);

      return response?.status;
    } finally {
      dispatch(setAuthLoading(false));
    }
  };

export const deleteFile = (id: string) => async (dispatch: TypedDispatch) => {
  try {
    dispatch(setAuthLoading(true));

    return await deleteFileRequest(id);
  } finally {
    dispatch(setAuthLoading(false));
  }
};

export const deleteFiles = (ids: string[]) => async (dispatch: TypedDispatch) => {
  try {
    dispatch(setAuthLoading(true));

    return await deleteFilesRequest(ids);
  } finally {
    dispatch(setAuthLoading(false));
  }
};

export const createScreen =
  (data: CreateScreenRequestType) => async (dispatch: TypedDispatch) => {
    try {
      dispatch(setAuthLoading(true));

      return await createScreenRequest(data);
    } finally {
      dispatch(setAuthLoading(false));
    }
  };

export const startDownloadVideoFile =
  (data: StartVideoFileRequestType) => async (dispatch: TypedDispatch) => {
    try {
      dispatch(setAuthLoading(true));

      return await startDownloadVideoFileRequest(data);
    } finally {
      dispatch(setAuthLoading(false));
    }
  };

export const partDownloadVideoFile =
  (data: PartVideoFileRequestType) => async (dispatch: TypedDispatch) => {
    try {
      dispatch(setAuthLoading(true));

      return await partDownloadVideoFileRequest(data);
    } finally {
      dispatch(setAuthLoading(false));
    }
  };

export const endDownloadVideoFile =
  (data: EndVideoFileRequestType) => async (dispatch: TypedDispatch) => {
    try {
      dispatch(setAuthLoading(true));

      return await endDownloadVideoFileRequest(data);
    } finally {
      dispatch(setAuthLoading(false));
    }
  };

export const abortVideoUpload =
  (data: AbortVideoFileRequestType) => async (dispatch: TypedDispatch) => {
    try {
      dispatch(setAuthLoading(true));

      return await abortVideoUploadRequest(data);
    } finally {
      dispatch(setAuthLoading(false));
    }
  };
